import { roundCents } from '@/utils/money'
import type { CalculatorContext } from '@/types/calculator'
import type { RentalYieldInputs, RentalYieldResult } from './types'

export function calculateRentalYield(
  inputs: RentalYieldInputs,
  _context: CalculatorContext
): RentalYieldResult {
  const bondAmount = Math.max(0, inputs.purchasePrice - inputs.deposit)
  const grossAnnualRent = inputs.monthlyRent * 12
  const vacancyLoss = grossAnnualRent * inputs.vacancyRate
  const effectiveGrossIncome = grossAnnualRent - vacancyLoss

  const maintenanceCost = grossAnnualRent * inputs.maintenancePercent
  const managementCost = effectiveGrossIncome * inputs.managementPercent
  const operatingExpenses =
    maintenanceCost + managementCost + inputs.leviesMonthly * 12 + inputs.ratesMonthly * 12

  const netOperatingIncome = effectiveGrossIncome - operatingExpenses

  let monthlyBondPayment = 0
  let annualBondPayment = 0
  let annualInterestFirstYear = 0

  if (bondAmount > 0 && inputs.bondInterestRate > 0) {
    const r = inputs.bondInterestRate / 12
    const n = inputs.bondTermYears * 12
    const factor = Math.pow(1 + r, n)
    monthlyBondPayment = (bondAmount * r * factor) / (factor - 1)
    annualBondPayment = monthlyBondPayment * 12
    annualInterestFirstYear =
      bondAmount * inputs.bondInterestRate -
      (annualBondPayment - bondAmount * inputs.bondInterestRate)
    // Simplified first-year interest approximation: average balance ~ bondAmount - principal reduction
    annualInterestFirstYear = bondAmount * inputs.bondInterestRate * 0.95
  }

  const annualCashFlow = netOperatingIncome - annualBondPayment
  const cashOnCashReturn = inputs.deposit > 0 ? annualCashFlow / inputs.deposit : 0

  const grossYield = inputs.purchasePrice > 0 ? grossAnnualRent / inputs.purchasePrice : 0
  const netYield = inputs.purchasePrice > 0 ? netOperatingIncome / inputs.purchasePrice : 0

  return {
    grossAnnualRent: roundCents(grossAnnualRent),
    vacancyLoss: roundCents(vacancyLoss),
    effectiveGrossIncome: roundCents(effectiveGrossIncome),
    operatingExpenses: roundCents(operatingExpenses),
    bondAmount: roundCents(bondAmount),
    monthlyBondPayment: roundCents(monthlyBondPayment),
    annualBondPayment: roundCents(annualBondPayment),
    annualInterestFirstYear: roundCents(annualInterestFirstYear),
    netOperatingIncome: roundCents(netOperatingIncome),
    annualCashFlow: roundCents(annualCashFlow),
    cashOnCashReturn,
    grossYield,
    netYield,
  }
}
